// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useEffect } from 'react'
import { UserCog, Loader2, Check } from 'lucide-react'
import * as api from '../../api/client'

/**
 * Nurse assignment control — assign / reassign a nurse to a Plan of Care or enrollment.
 * Nurses are OwlRx user accounts; this lists them and writes assigned_nurse_id.
 */
export default function NurseAssign({ currentNurseId, onAssign }: {
  currentNurseId?: string | null
  onAssign: (nurseId: string) => Promise<void>
}) {
  const [users, setUsers] = useState<api.UserItem[]>([])
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!open || loaded) return
    api.listUsers().then(d => { setUsers(d.items); setLoaded(true) }).catch(() => setLoaded(true))
  }, [open, loaded])

  const current = users.find(u => u.id === currentNurseId)
  const assign = async (id: string) => {
    setBusy(true)
    try { await onAssign(id); setOpen(false) } finally { setBusy(false) }
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" style={btn} onClick={() => setOpen(o => !o)} disabled={busy}>
        {busy ? <Loader2 className="spin" size={14} /> : <UserCog size={14} />}
        {currentNurseId ? `Nurse: ${current?.display_name || 'assigned'}` : 'Assign nurse'}
      </button>
      {open && (
        <div style={dropdown}>
          <div style={{ padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: .4, color: 'var(--color-text-mute)', borderBottom: '1px solid var(--color-border)' }}>
            {currentNurseId ? 'Reassign to' : 'Assign to'}
          </div>
          {!loaded ? (
            <div style={{ padding: 12, display: 'flex', gap: 6, alignItems: 'center', color: 'var(--color-text-mute)', fontSize: 13 }}><Loader2 className="spin" size={14} /> loading…</div>
          ) : users.length === 0 ? (
            <div style={{ padding: 12, color: 'var(--color-text-mute)', fontSize: 13 }}>No users available.</div>
          ) : users.map(u => (
            <button key={u.id} type="button" style={option} onClick={() => assign(u.id)}>
              <span className="email-text">{u.display_name || u.email}</span>
              {u.id === currentNurseId && <Check size={14} style={{ color: 'var(--color-primary)' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 13, cursor: 'pointer' }
const dropdown: React.CSSProperties = { position: 'absolute', top: '100%', left: 0, marginTop: 4, minWidth: 240, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, boxShadow: '0 4px 14px rgba(0,0,0,0.14)', zIndex: 30, maxHeight: 300, overflowY: 'auto' }
const option: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left', padding: '9px 12px', border: 'none', borderBottom: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', color: 'var(--color-text)', fontSize: 13 }
